import type { Conversation, Thread, ThreadClosedReason } from './types';

/**
 * Every string the messaging screens render, and the small functions that compose them.
 *
 * Constants rather than literals, on this app's own precedent: a sentence with a test of its own
 * is a sentence somebody can rule on, and three of the ones below are answers to Sumit's rulings
 * of 2026-09-11 rather than copy.
 */

/** `MEM-055` AC-1's destination, and the thread beneath it. */
export const MESSAGES_PATH = '/app/messages';
export function threadPath(connectionRef: string): string {
  return `${MESSAGES_PATH}/${encodeURIComponent(connectionRef)}`;
}

/** `Web App.dc.html` §2.9's heading and its sentence. */
export const MESSAGES_TITLE = 'Messages';
export const MESSAGES_INTRO =
  'Every conversation in one place — follow-ups you sent and replies from your connections.';

/**
 * `MEM-055` AC-7's empty state, and it says **how a conversation starts** rather than that there
 * is nothing here.
 *
 * The prototype's sentence is *"No messages yet. Send a follow-up and replies will show up
 * here."* — which is true of the prototype, where the only way to start a thread is a follow-up
 * opener. It is not true of the product: `MEM-054` AC-2 puts Message on every connection, and
 * `MEM-057`'s profile code makes connections with no event and therefore no follow-up at all. A
 * member with no follow-ups reading that sentence would conclude, wrongly, that they cannot write
 * to anybody.
 */
export const NO_MESSAGES_TITLE = 'No messages yet';
export const NO_MESSAGES_DESCRIPTION =
  'Message anyone you have connected with, or send a follow-up opener after an event — replies land here.';

/** The *New* pill on an unread row (`MEM-055` AC-3, §3.13). */
export const NEW_PILL_LABEL = 'New';

/** The row's preview when its author's account is gone, or when nobody has written yet. */
export const NO_PREVIEW_YET = 'No messages yet';

/** `MEM-054` AC-2's controls. */
export const MESSAGE_ACTION_LABEL = 'Message';
export const SEND_LABEL = 'Send';
export const COMPOSER_PLACEHOLDER = 'Write a message…';
export const COMPOSER_LABEL = 'Write a message';
export const OLDER_MESSAGES_LABEL = 'Load earlier messages';
export const THREAD_BACK_LABEL = 'Messages';

/** §2.11's Messages card on the connection detail. */
export const MESSAGES_CARD_TITLE = 'Messages';
export const NO_THREAD_YET = 'No messages yet. Say hello below.';
export const OPEN_FULL_THREAD_LABEL = 'Open the full conversation';

/**
 * **Why a thread is read-only**, and the two sentences are Sumit's rulings of 2026-09-11 rather
 * than copy somebody chose.
 *
 *   - *"keep messages read only"* — **both** members keep the thread after a disconnect, not only
 *     the one who did not remove it (`MEM-054` AC-5, extending `OI-11`). The sentence therefore
 *     says *"you are no longer connected"* and not *"they disconnected"*: the removal is silent
 *     (`MEM-034` AC-2), and a line naming who did it would undo that in the one place it would be
 *     read.
 *   - The deleted account (`MEM-054` AC-7, AC-7's *"ok"*): the member keeps **their own**
 *     messages, and the line says the account no longer exists. It does not say *"deleted"* of a
 *     person, and it does not apologise — it states the fact that explains the gaps.
 */
export const DISCONNECTED_NOTICE =
  'You are no longer connected, so this conversation is read-only. Your messages stay here.';
export const ACCOUNT_DELETED_NOTICE =
  'This account no longer exists. Their messages went with it; yours stay here, read-only.';

export function closedNoticeFor(reason: ThreadClosedReason | null): string | null {
  if (reason === 'disconnected') return DISCONNECTED_NOTICE;
  if (reason === 'account-deleted') return ACCOUNT_DELETED_NOTICE;
  return null;
}

/** What the composer says when a send comes back refused. */
export const SEND_FAILED = 'That message did not send.';
export const SEND_REFUSED_DISCONNECTED =
  'You are no longer connected, so this conversation is read-only.';
export const SEND_REFUSED_RATE_LIMITED =
  'You have started a lot of new conversations today. Try the rest tomorrow.';

/** A person's whole name, for a row and a thread header. */
export function personName(person: {
  readonly firstName: string;
  readonly lastName: string | null;
}): string {
  return [person.firstName, person.lastName].filter((part) => (part ?? '').length > 0).join(' ');
}

/** `MEM-CON-013`'s *role* line. `null` when neither half resolves, so no empty line is drawn. */
export function roleAtCompany(person: {
  readonly role: string | null;
  readonly company: string | null;
}): string | null {
  const parts = [person.role, person.company]
    .map((part) => (part ?? '').trim())
    .filter((part) => part.length > 0);
  return parts.length === 0 ? null : parts.join(', ');
}

/** Initials for the avatar. Falls back to the mark rather than to an empty circle. */
export function initialsOf(person: {
  readonly firstName: string;
  readonly lastName: string | null;
}): string {
  const first = (person.firstName ?? '').trim().charAt(0);
  const last = (person.lastName ?? '').trim().charAt(0);
  const initials = `${first}${last}`.toUpperCase();
  return initials.length === 0 ? 'H' : initials;
}

/**
 * The thread's subtitle — §2.16's *"Met at {event}"*, or *"Connected"* where there is no event.
 *
 * `MEM-057`'s profile connect code makes a connection outside an event, so the second branch is
 * not a fallback for a resolution failure: it is a real state the product will have.
 */
export function threadSubtitle(thread: Pick<Thread, 'eventName'>): string {
  return thread.eventName === null ? 'Connected' : `Met at ${thread.eventName}`;
}

/**
 * When a thread last moved, as §3.13's row writes it.
 *
 * The prototype's `msgStamp` gives *"Nov 15 · 9:12 AM"* or the literal *"Today"*. This gives the
 * **time** for today and the **date** otherwise, which is the same intent without the redundancy:
 * a row that says *"Today"* and nothing else is a row two of which cannot be told apart, and the
 * prototype's own fixture has three.
 *
 * In the reader's timezone, never the event's — a message is not an event, and
 * `time-and-timezone.md`'s rule is that a business date is derived deliberately rather than by
 * accident. `null` for an unparseable instant, so nothing renders rather than *"Invalid Date"*.
 */
export function messageStamp(instant: string, reader: string, now: Date = new Date()): string | null {
  const at = new Date(instant);
  if (Number.isNaN(at.getTime())) return null;
  try {
    const day = new Intl.DateTimeFormat('en-US', { timeZone: reader, dateStyle: 'short' });
    if (day.format(at) === day.format(now)) {
      return new Intl.DateTimeFormat('en-US', {
        timeZone: reader,
        hour: 'numeric',
        minute: '2-digit',
      }).format(at);
    }
    const sameYear = at.getUTCFullYear() === now.getUTCFullYear();
    return new Intl.DateTimeFormat('en-US', {
      timeZone: reader,
      month: 'short',
      day: 'numeric',
      ...(sameYear ? {} : { year: 'numeric' }),
    }).format(at);
  } catch {
    return null;
  }
}

/** A row's accessible name, so a screen reader gets the person, the state and the preview. */
export function conversationRowLabel(conversation: Conversation): string {
  const parts = [personName(conversation)];
  if (conversation.unreadCount > 0) {
    parts.push(
      conversation.unreadCount === 1 ? '1 new message' : `${String(conversation.unreadCount)} new messages`,
    );
  }
  if (conversation.eventName !== null) parts.push(`Met at ${conversation.eventName}`);
  if (conversation.preview !== null) {
    parts.push(conversation.lastMessageIsMine ? `You: ${conversation.preview}` : conversation.preview);
  }
  return parts.join('. ');
}

/**
 * **Does any thread hold something this member has not read?** (`MEM-055` AC-4.)
 *
 * A boolean and not a count, deliberately: the navigation carries a dot, and
 * `hasOutstandingFollowUps` next door makes the same choice for the same reason — *"a count is a
 * second place the total is stated and a second place it can be wrong"*.
 */
export function hasUnreadThreads(
  conversations: ReadonlyArray<Conversation> | undefined,
): boolean {
  return (conversations ?? []).some((conversation) => conversation.unreadCount > 0);
}
